// /audit — 稽核紀錄: SEARCH-BY-TARGET (not a free list).
//
// GET /v3/admin/audit-logs is TARGETED (FACTS PACK §3d): exactly ONE of
// target_id | actor_user_id | case_id, else 400. So this screen is a SEARCH form,
// never a browse list — made explicit in the UI copy.
//
// Four search modes map onto the three real axes (target_id is PREFIXED live):
//   目標使用者  → target_id = "user:<id>"
//   目標裝置    → target_id = "device:<id>"
//   操作者      → actor_user_id = "<id>"
//   案件編號    → case_id = "<id>"
// Plus optional `action` + date-range (from/to in µs) filters; cursor paging.
//
// Deep-link: device/user detail pages link here with a query string
// (?target_id=device:... or ?actor_user_id=...). On mount we read those params,
// preset the matching mode + value, and auto-run the search.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../lib/api';
import { formatRelativeUs, formatTimestampUs } from '../../lib/format';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { actionLabel, actionTone, actorRoleLabel, splitTargetId, targetTypeLabel } from './auditModel';
import { queryAuditLogs } from './auditApi';
import type { AuditAxis, AuditLog } from './auditApi';
import '../_shared/shared.css';
import './AuditPage.css';

type Mode = 'target_user' | 'target_device' | 'actor' | 'case';

interface ModeDef {
  key: Mode;
  label: string;
  placeholder: string;
  axis: AuditAxis;
  prefix?: string; // applied to the value before sending (target_id is prefixed)
  icon: string; // 查詢對象語意圖示(步驟①一眼分辨)
}

const MODES: ModeDef[] = [
  { key: 'target_user', label: '目標使用者', placeholder: '使用者 ID，如 u-apac-01...', axis: 'target_id', prefix: 'user:', icon: '👤' },
  { key: 'target_device', label: '目標裝置', placeholder: '裝置 ID，如 device-e2e-001', axis: 'target_id', prefix: 'device:', icon: '📟' },
  { key: 'actor', label: '操作者', placeholder: '操作者使用者 ID', axis: 'actor_user_id', icon: '🛡️' },
  { key: 'case', label: '案件編號', placeholder: '案件編號，如 CS-1234', axis: 'case_id', icon: '🗂️' },
];

const PAGE_SIZE = 50;

/** Build the {axis, value} the API expects from the current mode + raw input. */
function resolveAxisValue(mode: ModeDef, raw: string): { axis: AuditAxis; value: string } {
  const trimmed = raw.trim();
  // if the user already typed the prefix, don't double it
  if (mode.prefix && !trimmed.startsWith(mode.prefix)) {
    return { axis: mode.axis, value: mode.prefix + trimmed };
  }
  return { axis: mode.axis, value: trimmed };
}

/** Reverse: given an incoming target_id/actor_user_id/case_id param, pick a mode + bare value. */
function modeFromParams(params: URLSearchParams): { mode: Mode; value: string } | null {
  const t = params.get('target_id');
  if (t) {
    if (t.startsWith('device:')) return { mode: 'target_device', value: t.slice('device:'.length) };
    if (t.startsWith('user:')) return { mode: 'target_user', value: t.slice('user:'.length) };
    return { mode: 'target_device', value: t }; // unprefixed → treat raw
  }
  const a = params.get('actor_user_id');
  if (a) return { mode: 'actor', value: a };
  const c = params.get('case_id');
  if (c) return { mode: 'case', value: c };
  return null;
}

// Round-trip the date-range filters through the URL as µs epoch (`from`/`to`, the
// API's own param names) so a shared/bookmarked/reloaded audit query keeps its
// window instead of silently widening to all-time. Stored as the µs the API got;
// restored back to the yyyy-mm-dd the <input type="date"> shows (local-day).
function dateInputFromUs(param: string | null): string {
  if (!param) return '';
  const us = Number(param);
  if (!Number.isFinite(us) || us <= 0) return '';
  const d = new Date(us / 1000);
  if (Number.isNaN(d.getTime())) return '';
  // local yyyy-mm-dd (matches how dateToUs interprets the input)
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

interface QueryState {
  loading: boolean;
  error: unknown;
  logs: AuditLog[] | null;
  // committed query echo (so the results header shows what was searched)
  label: string;
}

export default function AuditPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setMode] = useState<Mode>('target_device');
  const [value, setValue] = useState('');
  const [action, setAction] = useState('');
  const [fromDate, setFromDate] = useState(''); // yyyy-mm-dd (local)
  const [toDate, setToDate] = useState('');
  const [advOpen, setAdvOpen] = useState(false); // 進階篩選 disclosure 開合(純呈現)

  const [state, setState] = useState<QueryState>({ loading: false, error: null, logs: null, label: '' });

  // cursor stack for prev/next
  const [pageCursors, setPageCursors] = useState<(string | undefined)[]>([undefined]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const reqSeq = useRef(0);
  const didInitFromParams = useRef(false);

  const modeDef = useMemo(() => MODES.find((m) => m.key === mode)!, [mode]);

  // yyyy-mm-dd (local) → µs epoch. `to` snaps to end-of-day so the range is inclusive.
  function dateToUs(d: string, endOfDay: boolean): number | undefined {
    if (!d) return undefined;
    const ms = new Date(d + (endOfDay ? 'T23:59:59.999' : 'T00:00:00')).getTime();
    return Number.isFinite(ms) ? ms * 1000 : undefined;
  }

  const runQuery = useCallback(
    async (rawValue: string, m: ModeDef, cursor: string | undefined, opts: { action: string; fromDate: string; toDate: string }) => {
      const seq = ++reqSeq.current;
      const { axis, value: axisValue } = resolveAxisValue(m, rawValue);
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const page = await queryAuditLogs({
          axis,
          value: axisValue,
          action: opts.action.trim() || undefined,
          fromUs: dateToUs(opts.fromDate, false),
          toUs: dateToUs(opts.toDate, true),
          limit: PAGE_SIZE,
          cursor,
        });
        if (seq !== reqSeq.current) return;
        setState({ loading: false, error: null, logs: page.logs, label: `${m.label}：${axisValue}` });
        setNextCursor(page.nextCursor);
      } catch (e) {
        if (seq !== reqSeq.current) return;
        setState({ loading: false, error: e instanceof ApiError ? e : new Error('查詢失敗'), logs: null, label: '' });
        setNextCursor(null);
      }
    },
    []
  );

  // first page (resets cursor stack)
  const submit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!value.trim()) return;
      setPageCursors([undefined]);
      // reflect the search in the URL so it's shareable / back-button friendly
      const { axis, value: axisValue } = resolveAxisValue(modeDef, value);
      const next = new URLSearchParams();
      next.set(axis, axisValue);
      if (action.trim()) next.set('action', action.trim());
      // carry the date window too (as µs, the API's own param names) so a reloaded
      // or shared URL keeps its range instead of widening to all-time
      const fromUs = dateToUs(fromDate, false);
      const toUs = dateToUs(toDate, true);
      if (fromUs !== undefined) next.set('from', String(fromUs));
      if (toUs !== undefined) next.set('to', String(toUs));
      setSearchParams(next, { replace: true });
      void runQuery(value, modeDef, undefined, { action, fromDate, toDate });
    },
    [value, modeDef, action, fromDate, toDate, runQuery, setSearchParams]
  );

  const goNext = useCallback(() => {
    if (!nextCursor) return;
    setPageCursors((s) => [...s, nextCursor]);
    void runQuery(value, modeDef, nextCursor, { action, fromDate, toDate });
  }, [nextCursor, value, modeDef, action, fromDate, toDate, runQuery]);

  const goPrev = useCallback(() => {
    setPageCursors((s) => {
      if (s.length <= 1) return s;
      const reduced = s.slice(0, -1);
      void runQuery(value, modeDef, reduced[reduced.length - 1], { action, fromDate, toDate });
      return reduced;
    });
  }, [value, modeDef, action, fromDate, toDate, runQuery]);

  // deep-link: read query params once on mount; preset mode+value and auto-run.
  useEffect(() => {
    if (didInitFromParams.current) return;
    didInitFromParams.current = true;
    const fromParams = modeFromParams(searchParams);
    if (!fromParams) return;
    const md = MODES.find((m) => m.key === fromParams.mode)!;
    const incomingAction = searchParams.get('action') ?? '';
    // restore the date window from the URL (µs → date-input yyyy-mm-dd)
    const incomingFrom = dateInputFromUs(searchParams.get('from'));
    const incomingTo = dateInputFromUs(searchParams.get('to'));
    setMode(fromParams.mode);
    setValue(fromParams.value);
    setAction(incomingAction);
    setFromDate(incomingFrom);
    setToDate(incomingTo);
    // 帶進階條件的深連 → 自動展開進階區,讓操作者看見已套用的篩選
    if (incomingAction || incomingFrom || incomingTo) setAdvOpen(true);
    setPageCursors([undefined]);
    void runQuery(fromParams.value, md, undefined, { action: incomingAction, fromDate: incomingFrom, toDate: incomingTo });
  }, [searchParams, runQuery]);

  const logs = state.logs;
  const hasSearched = state.label !== '' || state.loading || state.error != null;
  const pageIndex = pageCursors.length - 1;
  const advActive = Boolean(action.trim() || fromDate || toDate); // 進階區是否已套用條件

  return (
    <div className="audit-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">稽核紀錄</h1>
          <p className="page-subtitle">
            此頁為「依目標查詢」，非清單瀏覽：每次查詢必須鎖定一個對象（使用者／裝置／操作者／案件擇一）。
          </p>
        </div>
      </div>

      {/* ---- 查詢建構列(三段式:①對象 → ②識別值 → ③進階篩選)---- */}
      <form className="card audit-query-card" onSubmit={submit} role="search">
        {/* ① 選擇查詢對象 */}
        <div className="audit-step">
          <span className="audit-step-label">
            <span className="audit-step-num">1</span>
            選擇查詢對象
          </span>
          <div className="adm-axis-group" role="radiogroup" aria-label="查詢對象">
            {MODES.map((m) => (
              <label key={m.key} className={`adm-axis-opt${mode === m.key ? ' is-active' : ''}`}>
                <input
                  type="radio"
                  name="audit-mode"
                  value={m.key}
                  checked={mode === m.key}
                  onChange={() => {
                    setMode(m.key);
                  }}
                />
                <span className="audit-axis-icon" aria-hidden="true">
                  {m.icon}
                </span>
                {m.label}
              </label>
            ))}
          </div>
        </div>

        {/* ② 輸入識別值(主輸入 + 送出) */}
        <div className="audit-step">
          <span className="audit-step-label">
            <span className="audit-step-num">2</span>
            輸入「{modeDef.label}」的識別值
          </span>
          <div className="audit-value-row">
            <div className="audit-value-field">
              <span className="audit-value-field-icon" aria-hidden="true">
                🔍
              </span>
              <input
                type="search"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={modeDef.placeholder}
                aria-label={`${modeDef.label} 查詢值`}
              />
            </div>
            <button type="submit" className="btn-primary audit-submit-btn" disabled={!value.trim() || state.loading}>
              {state.loading ? '查詢中…' : '查詢'}
            </button>
          </div>
          {modeDef.prefix && (
            <p className="audit-value-hint">
              <span aria-hidden="true">ⓘ</span>
              系統會自動加上「{modeDef.prefix}」前綴，直接輸入 ID 即可（也可貼上完整的 {modeDef.prefix}… 值）。
            </p>
          )}
        </div>

        {/* ③ 進階篩選(動作 / 日期範圍),收合式 */}
        <details className="collapse" open={advOpen} onToggle={(e) => setAdvOpen(e.currentTarget.open)}>
          <summary>
            <span className="audit-adv-summary">
              <span className="audit-step-num">3</span>
              進階篩選（動作／日期範圍）
              {advActive && <span className="pill pill-info pill-dot audit-adv-badge">已套用</span>}
            </span>
          </summary>
          <div className="collapse-body">
            <div className="audit-adv-body">
              <label className="adm-field">
                <span className="text-secondary adm-field-label">動作篩選（選填）</span>
                <input
                  type="text"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  placeholder="如 device.disable"
                  aria-label="動作篩選"
                />
              </label>
              <label className="adm-field">
                <span className="text-secondary adm-field-label">起始日期（選填）</span>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </label>
              <label className="adm-field">
                <span className="text-secondary adm-field-label">結束日期（選填）</span>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </label>
            </div>
          </div>
        </details>
      </form>

      {/* ---- results ---- */}
      {!hasSearched && (
        <EmptyState
          icon="🔍"
          title="輸入查詢對象以開始"
          detail="稽核 API 不提供全域清單；請先選擇查詢對象並輸入 ID／案件編號，再執行查詢。也可從使用者或裝置的詳情頁直接帶入查詢。"
          action={
            <div className="audit-empty-actions">
              <Link className="btn-ghost btn-sm" to="/users">
                前往使用者清單
              </Link>
              <Link className="btn-ghost btn-sm" to="/devices">
                前往裝置清單
              </Link>
            </div>
          }
        />
      )}

      {state.loading && logs === null && <LoadingState label="查詢稽核紀錄…" />}

      {!state.loading && state.error != null && <ErrorState error={state.error} onRetry={() => submit()} />}

      {state.error == null && logs && logs.length === 0 && (
        <EmptyState
          icon="🗒️"
          title="沒有符合的稽核紀錄"
          detail={`查詢條件「${state.label}」沒有結果。確認 ID 是否正確，或放寬動作／日期篩選。`}
        />
      )}

      {state.error == null && logs && logs.length > 0 && (
        <>
          {/* 查詢條件摘要 chip bar:主數字 + 對象條件 + 頁次 */}
          <div className="audit-summary-chips">
            <span className="audit-summary-count">
              本頁 <strong>{logs.length}</strong> 筆
            </span>
            <span className="pill pill-neutral">{state.label}</span>
            <span className="text-tertiary">第 {pageIndex + 1} 頁</span>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>時間</th>
                  <th>動作</th>
                  <th>對象</th>
                  <th>操作者</th>
                  <th>詳情</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => {
                  const tgt = splitTargetId(log.target_id);
                  return (
                    <tr key={`${log.ts_us}-${log.action}-${i}`}>
                      <td className="adm-mono" title={formatTimestampUs(log.ts_us)} style={{ whiteSpace: 'nowrap' }}>
                        {formatRelativeUs(log.ts_us)}
                      </td>
                      <td>
                        <span className={`pill pill-${actionTone(log.action)}`}>{actionLabel(log.action)}</span>
                        <div className="audit-cell-sub adm-mono">{log.action}</div>
                      </td>
                      <td>
                        <div className="adm-cell-strong adm-mono" style={{ fontSize: 'var(--font-size-base)' }}>
                          {tgt.id}
                        </div>
                        <div className="audit-cell-sub">
                          {targetTypeLabel(log.target_type)}
                          {tgt.prefix && tgt.prefix !== log.target_type ? `・${tgt.prefix}` : ''}
                        </div>
                      </td>
                      <td>
                        <div className="adm-mono" style={{ fontSize: 'var(--font-size-base)' }}>
                          {log.actor_user_id || '—'}
                        </div>
                        <div className="audit-cell-sub">{actorRoleLabel(log.actor_role)}</div>
                      </td>
                      <td className="text-secondary" style={{ fontSize: 'var(--font-size-base)' }}>
                        {log.case_id && (
                          <div>
                            案件：
                            <Link
                              className="pill pill-info chip-link"
                              to={`/audit?case_id=${encodeURIComponent(log.case_id)}`}
                            >
                              {log.case_id}
                            </Link>
                          </div>
                        )}
                        {log.reason && <div className="audit-detail-reason">原因：{log.reason}</div>}
                        {!log.case_id && !log.reason && '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="audit-pager-group">
            <button className="btn-ghost btn-sm" onClick={goPrev} disabled={pageIndex === 0 || state.loading}>
              ← 上一頁
            </button>
            <span className="audit-pager-current text-secondary">第 {pageIndex + 1} 頁</span>
            <button className="btn-ghost btn-sm" onClick={goNext} disabled={!nextCursor || state.loading}>
              下一頁 →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
